/**
 * FOODHUB Wishlist System
 *
 * Complete localStorage-backed wishlist management for FOODHUB Fine Food Supply.
 * Provides methods to manage product favorites and UI synchronization.
 *
 * Global API:
 *   FoodHub.Wishlist.toggle(handle) - Add/remove product
 *   FoodHub.Wishlist.isInWishlist(handle) - Check if product is favorited
 *   FoodHub.Wishlist.getAll() - Get array of all product handles
 *   FoodHub.Wishlist.getCount() - Get count of favorited products
 *   FoodHub.Wishlist.remove(handle) - Remove specific product
 *   FoodHub.Wishlist.clear() - Clear all favorites
 *
 * Events:
 *   foodhub:wishlist:updated - Dispatched with { count, handle, action }
 */

(function() {
  'use strict';

  // Ensure FoodHub namespace exists
  window.FoodHub = window.FoodHub || {};

  const STORAGE_KEY = 'foodhub_wishlist';
  const WISHLIST_BTN_SELECTOR = '.foodhub-wishlist-btn';
  const WISHLIST_BADGE_SELECTOR = '[data-wishlist-badge]';
  const TOAST_DURATION = 3000;

  /**
   * Wishlist Module
   */
  const Wishlist = {
    /**
     * Get wishlist array from localStorage
     * @returns {Array<string>} Array of product handles
     */
    _getWishlist() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.warn('FOODHUB Wishlist: Error reading localStorage', e);
        return [];
      }
    },

    /**
     * Save wishlist array to localStorage
     * @param {Array<string>} wishlist - Array of product handles
     */
    _saveWishlist(wishlist) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlist));
      } catch (e) {
        console.warn('FOODHUB Wishlist: Error writing to localStorage', e);
      }
    },

    /**
     * Toggle product in wishlist (add if not present, remove if present)
     * @param {string} handle - Product handle
     * @returns {boolean} True if product was added, false if removed
     */
    toggle(handle) {
      if (!handle || typeof handle !== 'string') {
        console.error('FOODHUB Wishlist: Invalid handle', handle);
        return false;
      }

      const wishlist = this._getWishlist();
      const index = wishlist.indexOf(handle);
      let isAdded = false;

      if (index > -1) {
        // Remove
        wishlist.splice(index, 1);
        this._showToast('Retire des favoris');
      } else {
        // Add
        wishlist.push(handle);
        isAdded = true;
        this._showToast('Ajoute aux favoris');
      }

      this._saveWishlist(wishlist);
      this._updateUI(handle, isAdded);
      this._dispatchEvent(handle, isAdded ? 'added' : 'removed', wishlist.length);

      return isAdded;
    },

    /**
     * Check if product is in wishlist
     * @param {string} handle - Product handle
     * @returns {boolean}
     */
    isInWishlist(handle) {
      return this._getWishlist().includes(handle);
    },

    /**
     * Get all product handles in wishlist
     * @returns {Array<string>}
     */
    getAll() {
      return this._getWishlist();
    },

    /**
     * Get count of products in wishlist
     * @returns {number}
     */
    getCount() {
      return this._getWishlist().length;
    },

    /**
     * Remove specific product from wishlist
     * @param {string} handle - Product handle
     */
    remove(handle) {
      const wishlist = this._getWishlist();
      const index = wishlist.indexOf(handle);

      if (index > -1) {
        wishlist.splice(index, 1);
        this._saveWishlist(wishlist);
        this._updateUI(handle, false);
        this._showToast('Retire des favoris');
        this._dispatchEvent(handle, 'removed', wishlist.length);
      }
    },

    /**
     * Clear all favorites
     */
    clear() {
      const wishlist = this._getWishlist();
      if (wishlist.length > 0) {
        localStorage.removeItem(STORAGE_KEY);
        this._updateAllButtons();
        this._updateBadge();
        this._showToast('Favoris vides');
        this._dispatchEvent(null, 'cleared', 0);
      }
    },

    /**
     * Update UI for a specific product
     * @param {string} handle - Product handle
     * @param {boolean} isAdded - Whether product was added
     */
    _updateUI(handle, isAdded) {
      const buttons = document.querySelectorAll(
        `${WISHLIST_BTN_SELECTOR}[data-wishlist-handle="${handle}"]`
      );
      buttons.forEach(btn => {
        if (isAdded) {
          btn.classList.add('active');
          btn.setAttribute('aria-pressed', 'true');
        } else {
          btn.classList.remove('active');
          btn.setAttribute('aria-pressed', 'false');
        }
      });
      this._updateBadge();
    },

    /**
     * Update all wishlist buttons on page
     */
    _updateAllButtons() {
      const wishlist = this._getWishlist();
      const buttons = document.querySelectorAll(WISHLIST_BTN_SELECTOR);

      buttons.forEach(btn => {
        const handle = btn.getAttribute('data-wishlist-handle');
        const isInWishlist = wishlist.includes(handle);

        if (isInWishlist) {
          btn.classList.add('active');
          btn.setAttribute('aria-pressed', 'true');
        } else {
          btn.classList.remove('active');
          btn.setAttribute('aria-pressed', 'false');
        }
      });
    },

    /**
     * Update wishlist count badge
     */
    _updateBadge() {
      const badges = document.querySelectorAll(WISHLIST_BADGE_SELECTOR);
      const count = this.getCount();

      badges.forEach(badge => {
        badge.textContent = count;
        if (count > 0) {
          badge.style.display = 'inline-block';
        } else {
          badge.style.display = 'none';
        }
      });
    },

    /**
     * Show toast notification
     * @param {string} message - Message to display
     */
    _showToast(message) {
      // Create toast container if it doesn't exist
      let toastContainer = document.getElementById('foodhub-toast-container');
      if (!toastContainer) {
        toastContainer = document.createElement('div');
        toastContainer.id = 'foodhub-toast-container';
        toastContainer.style.cssText = `
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 9999;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        `;
        document.body.appendChild(toastContainer);
      }

      // Create toast element
      const toast = document.createElement('div');
      toast.className = 'foodhub-toast';
      toast.textContent = message;
      toast.style.cssText = `
        background-color: #1B6B5A;
        color: white;
        padding: 12px 16px;
        border-radius: 4px;
        margin-bottom: 10px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        animation: slideInUp 0.3s ease-out;
        font-size: 14px;
        line-height: 1.4;
      `;

      toastContainer.appendChild(toast);

      // Add animation keyframes if not already present
      if (!document.querySelector('style[data-foodhub-toast-animation]')) {
        const style = document.createElement('style');
        style.setAttribute('data-foodhub-toast-animation', 'true');
        style.textContent = `
          @keyframes slideInUp {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          @keyframes slideOutDown {
            from {
              transform: translateY(0);
              opacity: 1;
            }
            to {
              transform: translateY(100%);
              opacity: 0;
            }
          }
          .foodhub-toast-exit {
            animation: slideOutDown 0.3s ease-in forwards;
          }
        `;
        document.head.appendChild(style);
      }

      // Auto-remove toast
      setTimeout(() => {
        toast.classList.add('foodhub-toast-exit');
        setTimeout(() => toast.remove(), 300);
      }, TOAST_DURATION);
    },

    /**
     * Dispatch custom event
     * @param {string|null} handle - Product handle (null for bulk operations)
     * @param {string} action - Action type: 'added', 'removed', 'cleared'
     * @param {number} count - Current wishlist count
     */
    _dispatchEvent(handle, action, count) {
      const event = new CustomEvent('foodhub:wishlist:updated', {
        detail: {
          count,
          handle,
          action,
          timestamp: new Date().toISOString()
        }
      });
      document.dispatchEvent(event);
    },

    /**
     * Initialize all wishlist buttons on page
     */
    init() {
      const buttons = document.querySelectorAll(WISHLIST_BTN_SELECTOR);

      buttons.forEach(btn => {
        const handle = btn.getAttribute('data-wishlist-handle');

        // Set initial state
        if (this.isInWishlist(handle)) {
          btn.classList.add('active');
          btn.setAttribute('aria-pressed', 'true');
        } else {
          btn.setAttribute('aria-pressed', 'false');
        }

        // Ensure onclick is set (in case Liquid didn't set it)
        if (!btn.onclick) {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggle(handle);
          });
        }
      });

      // Initialize badge
      this._updateBadge();
    }
  };

  // Assign to namespace
  window.FoodHub.Wishlist = Wishlist;

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      Wishlist.init();
    });
  } else {
    Wishlist.init();
  }

  // Also re-initialize when Shopify AJAX cart is updated (if using cart drawer)
  document.addEventListener('cart:updated', () => {
    Wishlist._updateAllButtons();
  });

})();
